// @components/form-elements/SmartSelectInput.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FixedSizeList as List } from 'react-window';
import { fetchJson } from '@utils/fetchJson';
import { API_BASE_URL } from '../../config';
import Cookies from 'js-cookie';
import { Check } from 'lucide-react';

import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  FloatingPortal,
  useDismiss,
  useInteractions,
  useRole,
  useListNavigation,
} from '@floating-ui/react';

import { useCreateEntityMap } from '@components/GlobalModals';

const SmartSelectInput = ({
  id,
  value,
  onSelect,
  onObjectSelect = {},
  zIndex = 9999,
  config,
}) => {
  const {
    type = 'category',
    source = '',
    label = '',
    check = '',
    list = '',
    placeholder = 'Select...',
    allowAddNew = false,
    preload = false,
    multi = false,
    getValue,
    getLabel,
    customURL = "",
    statusCheck = false,
    customParameters = {},
  } = config;

  const entityMap = useCreateEntityMap();
  const resolveStoreKey = (type) => {
  if (!type) return null;

  // auto convert: customer -> createCustomer
  //const cap = type.charAt(0).toUpperCase() + type.slice(1);
  return `${type}`;
};
  // ---------------- STATE ----------------
  const [options, setOptions] = useState([]);
  const [internalValue, setInternalValue] = useState(multi ? [] : null);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [parent_id, setParentId] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState("0");

  const [activeIndex, setActiveIndex] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const listRef = useRef(null);
  const inputRef = useRef(null);
  const listItemsRef = useRef([]);

  const cacheStore = useMemo(() => new Map(), []);
  const key = `${type}-${source}`;

  // ---------------- NORMALIZER ----------------
  const normalizeOptions = (items = []) =>
    items.map(item => ({
      value: getValue ? getValue(item) : item.id,
      label: getLabel ? getLabel(item) : item.name || '',
      original: item,
    }));

  // ---------------- FETCH DATA ----------------
  const fetchOptions = async (pageValue = 0) => {
    setLoading(true);
    const headers = {
      token: Cookies.get('_bb_key'),
      SadminID: Cookies.get('authid'),
    };

    let res = {}, data = [];

    if (type === "category") {
      const url = customURL || `${API_BASE_URL}/categorySlugList`;
      const postData = customURL ? customParameters : { status: "active", slug: source };

      res = await fetchJson(url, { method: "POST", headers, body: JSON.stringify(postData) });
      data = customURL ? res?.data || [] : res?.data?.[0]?.sublist || [];
      setParentId(res?.data?.[0]?.category_id);

    } else {
      res = await fetchJson(`${API_BASE_URL}/searchList`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          text: "",
          system: "new",
          tableName: type === "customer" ? "customer" : source,
          wherec: type === "customer" ? "name" : check,
          status: statusCheck,
          list,
          curpage: pageValue,
          ...customParameters,
        }),
      });
      data = res.data || [];
    }

    const normalized = normalizeOptions(data);

    setOptions(prev => {
      const existing = new Set(prev.map(i => i.value));
      return [...prev, ...normalized.filter(i => !existing.has(i.value))];
    });

    setHasMore(res?.loadstate);
    setPage(res?.paginginfo?.nextPage);
    cacheStore.set(key, normalized);
    setLoading(false);
  };

  // preload
  useEffect(() => {
    if (preload) fetchOptions();
  }, []);

  // update internalValue when external value changes
  useEffect(() => {
    const ids = value ? String(value).split(",").map(v => v.trim()).filter(Boolean) : [];

    if (!ids.length) {
      setInternalValue(multi ? [] : null);
      return;
    }

    const cached = cacheStore.get(key);
    if (cached?.length) {
      const matched = cached.filter(opt => ids.includes(String(opt.value)));
      setInternalValue(multi ? matched : matched[0]);
      return;
    }

    (async () => {
      await fetchOptions();
      const fresh = cacheStore.get(key) || [];
      const matched = fresh.filter(opt => ids.includes(String(opt.value)));
      setInternalValue(multi ? matched : matched[0]);
    })();
  }, [value]);

  // ---------------- FILTER ----------------
  const filtered = inputValue.trim()
    ? options.filter(opt => opt.label.toLowerCase().includes(inputValue.toLowerCase()))
    : options;

  useEffect(() => {
    listItemsRef.current = filtered.map((_, i) => listItemsRef.current[i] || null);
  }, [filtered]);

  // ---------------- FLOATING DROPDOWN ----------------
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(4), flip(), shift()],
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
  });

  const dismiss = useDismiss(context, { escapeKey: true, outsidePress: true });
  const role = useRole(context, { role: "listbox" });

  const listNav = useListNavigation(context, {
    listRef: listItemsRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
    loop: true,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    dismiss,
    role,
    listNav,
  ]);

  // auto-scroll active item
  useEffect(() => {
    if (listRef.current && activeIndex !== null && isOpen) {
      listRef.current.scrollToItem(activeIndex, "smart");
    }
  }, [activeIndex, isOpen]);

  // ---------------- SELECT ----------------
  const handleSelect = (item, index) => {
    if (multi) {
      let selected = [...internalValue];
      const exists = selected.find(v => v.value === item.value);

      selected = exists
        ? selected.filter(v => v.value !== item.value)
        : [...selected, item];

      setInternalValue(selected);
      onSelect(selected.map(i => i.value).join(','));
      onObjectSelect?.(item);

      setInputValue("");
      setActiveIndex(null);
      setTimeout(() => inputRef.current?.focus(), 0);
      return;
    }

    // SINGLE SELECT:
    setInternalValue(item);
    onSelect(item.value);
    onObjectSelect?.(item);

    setInputValue("");
    setIsOpen(false);
    setActiveIndex(null);

    // keep focus on input (so tab goes next field)
    setTimeout(() => inputRef.current?.focus(), 10);
  };

  // ---------------- KEYBOARD EVENTS ----------------
  const handleKeyDown = (e) => {
    //if (!isOpen) return;
    if (!isOpen || e.target === inputRef.current && inputValue.trim()) {
      // allow text typing without triggering list navigation
      if (["ArrowDown","ArrowUp"].includes(e.key)) return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (isOpen && activeIndex !== null && filtered[activeIndex]) {
        handleSelect(filtered[activeIndex], activeIndex);
      }
      return;
    }

    if (e.key === "Escape") {
      setIsOpen(false);
      setActiveIndex(null);
      return;
    }

    if (e.key === "Tab") {
      setIsOpen(false);
      return; // allow normal tab navigation
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();

      if (!isOpen) {
        setIsOpen(true);
        setActiveIndex(0);
        return;
      }

      const next = activeIndex === null ? 0 : Math.min(activeIndex + 1, filtered.length - 1);
      setActiveIndex(next);
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();

      if (!isOpen) {
        setIsOpen(true);
        setActiveIndex(filtered.length - 1);
        return;
      }

      const prev = activeIndex === null ? filtered.length - 1 : Math.max(activeIndex - 1, 0);
      setActiveIndex(prev);
      return;
    }
  };
  const removeChip = (item) => {
  const updated = internalValue.filter(v => v.value !== item.value);
  setInternalValue(updated);
  onSelect(updated.map(i => i.value).join(','));
};
  // ---------------- REFRESH ----------------
  const handleRefresh = () => {
    cacheStore.delete(key);
    setOptions([]);
    setPage(0);
    setHasMore(true);
    fetchOptions();
  };
  const capitalize = s => s.charAt(0).toUpperCase() + s.slice(1);
  // ---------------- ADD NEW ----------------
  const handleNew = async (rowData) => {
  try {
    setIsOpen(false);

    // const entityType = config?.type;
    
    // if (!entityType) {
    //   console.warn("⚠️ Missing entity type in config");
    //   return;
    // }

    const storeKey = resolveStoreKey(config?.type);
    const storeHook = entityMap?.[storeKey];
    if (!storeHook) {
      console.warn(`⚠️ No store registered for type: ${storeKey}`);
      return;
    }

    const storeState = storeHook.getState?.();
    if (!storeState) {
      console.warn(`⚠️ Store state unavailable for: ${storeKey}`);
      return;
    }

    // ✅ Support both modern & legacy patterns
    // const openCreate =
    //   storeState.open?.[`${entityType}Create`] ||
    //   storeState[`open${capitalize(entityType)}Create`];
    const capital = capitalize(config.type);

const openCreate =
  storeState[`openCreate${capital}`] ||      // openCreateCustomer
  storeState[`open${capital}Create`] ||      // openCustomerCreate
  storeState.open?.[`create${capital}`];     // open: { createCustomer() {} }


    if (typeof openCreate !== "function") {
      console.warn(`⚠️ No openCreate() method found for type: ${storeKey}`);
      return;
    }

    // 🧩 Execute the modal open and handle callback result
    openCreate({ payload: rowData }, async (result) => {
      if (!result) return;

      // 🧹 Clear cache + refetch
      cacheStore.delete(key);
      localStorage.removeItem(`recent_${key}`);
      await fetchOptions();

      const allOptions = cacheStore.get(key) || [];
      const lastID =
        result.last_insert_id || result.lastID || result.id || result.value;

      const selected = allOptions.find(
        (opt) => String(opt.value) === String(lastID)
      );

      if (selected) {
        onSelect?.(selected.value);
        onObjectSelect?.(selected.original);
      }
    });
  } catch (err) {
    console.error("❌ handleNew failed:", err);
  }
};
  // ---------------- SCROLL ----------------
  const handleScroll = () => {
    const el = listRef.current?._outerRef;
    if (!el) return;

    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      if (hasMore && !loading) {
        fetchOptions(page);
      }
    }
  };

  // ---------------- INPUT PROPS ----------------
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    if (!isOpen) setIsOpen(true);
    setActiveIndex(null);
  };

  const referenceProps = getReferenceProps({
    onKeyDown: handleKeyDown,
    onChange: handleInputChange,
    // onClick: (e) => {
    //   e.stopPropagation();
    //   if (!multi) {
    //     // single-select: DO NOT reopen dropdown on click if selection just happened
    //     e.stopPropagation();
    //     setActiveIndex(null);
    //   setIsOpen(true);
    //     setTimeout(() => inputRef.current?.focus(), 10);
    //   }else{

    //   }
    // },
    onClick:(e) => {
      e.stopPropagation();
      setIsOpen(true);
      setActiveIndex(null);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  });

  // ---------------- DISPLAY ----------------
  const getDisplayValue = () => {
    if (multi) return inputValue;
    if (inputValue) return inputValue;
    return internalValue?.label || "";
  };

  // ---------------- ROW ----------------
  const Row = ({ index, style }) => {
    const item = filtered[index];
    const isSelected = multi
      ? internalValue.some(v => v.value === item.value)
      : internalValue?.value === item.value;

    const isActive = index === activeIndex;

    return (
      <div
        ref={(node) => { listItemsRef.current[index] = node; }}
        role="option"
        tabIndex={isActive ? 0 : -1}
        aria-selected={isActive}
        style={{ ...style, background: isActive ? "#E8F0FE" : "transparent" }}
        {...getItemProps({
          onClick: () => handleSelect(item, index, false),
          onKeyDown: (e) => handleKeyDown(e),
        })}
        className="px-4 py-2 cursor-pointer hover:bg-gray-100 flex justify-between text-sm"
      >
        <span className="truncate">{item.label}</span>
        {isSelected && <Check size={16} className="text-green-600" />}
      </div>
    );
  };

  // ---------------- RENDER ----------------
  return (
    <div className="relative w-full">
      {label && <label className="block text-sm text-gray-700 mb-1">{label}</label>}

      {/* MULTI SELECT */}
      {multi ? (
        <div
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => {
            setTimeout(() => {
              setIsOpen(true);
              inputRef.current?.focus();
            }, 10);
          }}
          className="flex flex-wrap gap-1 rounded w-full border px-2 py-1 bg-gray-100 min-h-[42px]"
        >
          {internalValue.map((v, i) => (
            <span
              key={i}
              className="bg-blue-200 text-blue-800 px-2 py-1 rounded-full text-xs flex items-center"
            >
              {v.label}
              <button
                className="ml-1 hover:text-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  removeChip(v);
                }}
              >
                ×
              </button>
            </span>
          ))}

          <input
            ref={(el) => { inputRef.current = el; refs.setReference(el); }}
            value={inputValue}
            placeholder={placeholder}
            className="flex-grow min-w-[120px] h-10 bg-gray-100 focus:outline-none text-sm px-2 py-1"
            autoComplete="off"
            {...referenceProps}
          />
        </div>
      ) : (
        // SINGLE SELECT
        <div className="relative">
          <input
            id={id}
            ref={(el) => {
              inputRef.current = el;
              refs.setReference(el);
            }}
            value={getDisplayValue()}
            placeholder={placeholder}
            className="form-input w-full bg-gray-100 rounded px-3 py-2 text-sm"
            autoComplete="off"
            {...referenceProps}
          />

          {internalValue && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setInternalValue(null);
                onSelect("");
                setInputValue("");
                setTimeout(() => inputRef.current?.focus(), 10);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-500"
            >
              ×
            </button>
          )}
        </div>
      )}

      {/* DROPDOWN */}
      {isOpen && (
        <FloatingPortal root={document.body}>
  <div
    ref={refs.setFloating}
    style={{
      ...floatingStyles,
      zIndex,
      minWidth: refs.domReference.current?.offsetWidth || 200,
    }}
    {...getFloatingProps()}
    className="bg-white shadow-lg border rounded-md text-sm"   // ← FIXED
  >
    <div className="flex justify-between items-center px-3 py-2 bg-blue-50 border-b">
      <button
        type="button"
        onClick={handleRefresh}
        className="text-blue-600 text-xs"
      >
        Refresh
      </button>

      {allowAddNew && (
        <button
          onClick={() => {
            if (config.type === "category") {
              handleNew({
                is_parent: "no",
                short: true,
                form_label: placeholder,
                parent_id: parent_id,
              });
            } else {
              handleNew({});
            }
          }}
          className="hover:underline text-blue-600"
        >
          + Add New {label}
        </button>
      )}
    </div>

    {filtered.length === 0 ? (
      <div className="px-4 py-3 text-center text-gray-400">
        {loading ? "Loading..." : "No results found"}
      </div>
    ) : (
      <List
        ref={listRef}
        height={Math.min(filtered.length * 40, 240)} // ← scroll inside list only
        itemCount={filtered.length}
        itemSize={40}
        onScroll={handleScroll}
        width="100%"
      >
        {Row}
      </List>
    )}

    {loading && filtered.length > 0 && (
      <div className="p-2 text-center text-xs text-gray-400 border-t">
        Loading more...
      </div>
    )}
  </div>
</FloatingPortal>

      )}
    </div>
  );
};

export default SmartSelectInput;
